import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Modal,
  Form,
  message,
  Space,
  Tag,
  Popconfirm,
  Switch,
  DatePicker,
  Select,
  Alert,
  Row,
  Col,
  Statistic,
  Typography,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  SearchOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  BarChartOutlined,
  FireOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { whitelistApi, WhitelistIp, CreateWhitelistIpDto, UpdateWhitelistIpDto } from '../services/whitelistApi';
import { publicIpCheckApi } from '../services/publicIpCheckApi';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const Whitelist: React.FC = () => {
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingIp, setEditingIp] = useState<WhitelistIp | null>(null);
  const [checkIpModalVisible, setCheckIpModalVisible] = useState(false);
  const [checkIpValue, setCheckIpValue] = useState('');
  const [checkResult, setCheckResult] = useState<any>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkDeleteModalVisible, setBulkDeleteModalVisible] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    type: ''
  });
  const [searchInput, setSearchInput] = useState('');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch whitelist IPs with filters
  const { data: whitelistIpsRaw, isLoading, error, refetch, isFetching } = useQuery(
    ['whitelist-ips', refreshKey, filters],
    () => whitelistApi.getAll(filters),
    {
      enabled: true, // Enable automatic fetching
      refetchInterval: 30000, // Refetch every 30 seconds
      retry: 1,
      staleTime: 0, // Always consider data stale
      cacheTime: 0, // Don't cache data
      refetchOnWindowFocus: true, // Refetch when window gains focus
      refetchOnMount: true, // Refetch when component mounts
      onError: (error) => {
        // Error fetching whitelist IPs
      },
      onSuccess: (data) => {
        // Query success
      }
    }
  );

  // Ensure whitelistIps is always an array
  // Handle both direct array and wrapped response format
  const whitelistIps = Array.isArray(whitelistIpsRaw) 
    ? whitelistIpsRaw 
    : (whitelistIpsRaw && (whitelistIpsRaw as any).data && Array.isArray((whitelistIpsRaw as any).data)) 
      ? (whitelistIpsRaw as any).data 
      : [];
  

  // Force refetch on mount
  React.useEffect(() => {
    refetch();
  }, [refetch]);

  // Fetch statistics
  const { data: statsRaw } = useQuery(
    'whitelist-stats',
    whitelistApi.getStats,
    {
      retry: 1,
      onSuccess: (data) => {
      },
      onError: (error) => {
        console.error('Error fetching whitelist stats:', error);
      }
    }
  );

  // Extract stats from response
  const stats = statsRaw && (statsRaw as any).data ? (statsRaw as any).data : statsRaw;

  // Fetch failed login statistics
  const { data: failedLoginStatsRaw } = useQuery(
    'failed-login-stats',
    whitelistApi.getFailedLoginStats,
    {
      retry: 1,
      onSuccess: (data) => {
      },
      onError: (error) => {
        console.error('Error fetching failed login stats:', error);
      }
    }
  );

  // Extract failed login stats from response
  const failedLoginStats = failedLoginStatsRaw && (failedLoginStatsRaw as any).data ? (failedLoginStatsRaw as any).data : failedLoginStatsRaw;

  // Fetch whitelist status
  const { data: whitelistStatus } = useQuery(
    'whitelist-status',
    () => publicIpCheckApi.getWhitelistStatus(),
    {
      retry: 1,
      onError: (error) => {
        console.error('Error fetching whitelist status:', error);
      }
    }
  );

  // Create mutation
  const createMutation = useMutation(whitelistApi.create, {
    onMutate: async (newIp) => {
      console.log('Creating IP:', newIp);
      // Cancel any outgoing refetches
      await queryClient.cancelQueries('whitelist-ips');
      
      // Snapshot the previous value
      const previousIps = queryClient.getQueryData('whitelist-ips');
      
      // Optimistically update to the new value
      queryClient.setQueryData('whitelist-ips', (old: any) => {
        const newData = {
          ...newIp,
          _id: 'temp-' + Date.now(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isActive: true,
          createdBy: 'admin@admin.com'
        };
        return [...(Array.isArray(old) ? old : []), newData];
      });
      
      return { previousIps };
    },
    onSuccess: async (data) => {
      console.log('IP created successfully:', data);
      // Force refetch data
      await queryClient.invalidateQueries('whitelist-ips');
      await queryClient.invalidateQueries('whitelist-stats');
      // Force refresh by updating key
      setRefreshKey(prev => prev + 1);
      message.success('IP added to whitelist successfully!');
      setModalVisible(false);
      form.resetFields();
    },
    onError: (error: any, newIp, context) => {
      console.error('Error creating IP:', error);
      // Revert optimistic update
      if (context?.previousIps) {
        queryClient.setQueryData('whitelist-ips', context.previousIps);
      }
      message.error(error.response?.data?.message || 'Failed to add IP to whitelist');
    },
  });

  // Update mutation
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: UpdateWhitelistIpDto }) =>
      whitelistApi.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('whitelist-ips');
        queryClient.invalidateQueries('whitelist-stats');
        message.success('IP updated successfully!');
        setModalVisible(false);
        setEditingIp(null);
        form.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to update IP');
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(whitelistApi.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('whitelist-ips');
      queryClient.invalidateQueries('whitelist-stats');
      message.success('IP removed from whitelist successfully!');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to remove IP');
    },
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation(whitelistApi.bulkDelete, {
    onSuccess: (data) => {
      queryClient.invalidateQueries('whitelist-ips');
      queryClient.invalidateQueries('whitelist-stats');
      setSelectedRowKeys([]);
      setBulkDeleteModalVisible(false);
      message.success(`${data.deletedCount} IPs removed from whitelist!`);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to remove IPs');
    },
  });

  // Check IP mutation
  const checkIpMutation = useMutation(whitelistApi.checkIp, {
    onSuccess: (data) => {
      setCheckResult(data);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to check IP');
    },
  });

  const handleAdd = () => {
    setEditingIp(null);
    setModalVisible(true);
    form.resetFields();
  };

  const handleEdit = (ip: WhitelistIp) => {
    setEditingIp(ip);
    setModalVisible(true);
    form.setFieldsValue({
      ip: ip.ip,
      description: ip.description,
      type: ip.type,
      isActive: ip.isActive,
      expiresAt: ip.expiresAt ? dayjs(ip.expiresAt) : null,
    });
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleBulkDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select IPs to delete');
      return;
    }
    setBulkDeleteModalVisible(true);
  };

  // Filter handlers
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      search: '',
      type: ''
    });
    setSearchInput('');
  };

  // Throttling for search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.length >= 3 || searchInput.length === 0) {
        setFilters(prev => ({
          ...prev,
          search: searchInput
        }));
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchInput]);

  const confirmBulkDelete = () => {
    bulkDeleteMutation.mutate(selectedRowKeys as string[]);
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      console.log('Form values:', values);
      const data: CreateWhitelistIpDto | UpdateWhitelistIpDto = {
        ip: values.ip,
        description: values.description,
        type: values.type || 'single',
        isActive: values.isActive,
        expiresAt: values.expiresAt ? values.expiresAt.toISOString() : undefined,
      };

      console.log('Data to submit:', data);

      if (editingIp) {
        console.log('Updating IP:', editingIp._id);
        updateMutation.mutate({ id: editingIp._id, data });
      } else {
        console.log('Creating new IP');
        createMutation.mutate(data as CreateWhitelistIpDto);
      }
    });
  };

  const handleCheckIp = () => {
    if (checkIpValue.trim()) {
      checkIpMutation.mutate(checkIpValue.trim());
    }
  };

  const getStatusTag = (ip: WhitelistIp) => {
    if (!ip.isActive) {
      return <Tag color="red" icon={<CloseCircleOutlined />}>Inactive</Tag>;
    }
    
    if (ip.expiresAt && new Date(ip.expiresAt) < new Date()) {
      return <Tag color="orange" icon={<ClockCircleOutlined />}>Expired</Tag>;
    }
    
    return <Tag color="green" icon={<CheckCircleOutlined />}>Active</Tag>;
  };

  const getTypeTag = (type: string) => {
    return type === 'range' ? (
      <Tag color="blue" icon={<GlobalOutlined />}>Range</Tag>
    ) : (
      <Tag color="purple" icon={<SafetyOutlined />}>Single</Tag>
    );
  };

  const columns = [
    {
      title: 'IP Address',
      dataIndex: 'ip',
      key: 'ip',
      render: (ip: string, record: WhitelistIp) => (
        <Space>
          <Text code>{ip || 'N/A'}</Text>
          {getTypeTag(record?.type || 'single')}
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => description || <Text type="secondary">No description</Text>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: WhitelistIp) => record ? getStatusTag(record) : <Text type="secondary">Unknown</Text>,
    },
    {
      title: 'Failed Access',
      dataIndex: 'failedAccessCount',
      key: 'failedAccessCount',
      render: (count: number) => (
        <Tag color={count > 0 ? 'red' : 'green'}>
          {count || 0}
        </Tag>
      ),
    },
    {
      title: 'Expires',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : <Text type="secondary">Never</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: WhitelistIp) => record ? (
        <Space>
					<Button
						type="text"
						icon={<EditOutlined />}
						onClick={() => handleEdit(record)}
					/>
          <Popconfirm
            title="Are you sure you want to remove this IP from whitelist?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          > 
						<Button
							type="text"
							danger
							icon={<DeleteOutlined />}
						/>
          </Popconfirm>
        </Space>
      ) : null,
    },
  ];

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Error"
          description="Failed to load whitelist data"
          type="error"
          showIcon
        />
      </div>
    );
  }

  // Additional safety check
  if (!Array.isArray(whitelistIps)) {
    console.error('Whitelist data is not an array:', whitelistIps);
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Data Error"
          description="Whitelist data format is invalid"
          type="error"
          showIcon
        />
      </div>
    );
  }

  // Check if whitelist is enabled
  const isWhitelistEnabled = whitelistStatus?.enabled === true;

  return (
    <div style={{ padding: '24px' }}>
      {/* Whitelist Status Notification */}
      {whitelistStatus && !isWhitelistEnabled && (
        <Alert
          message="IP Whitelist Disabled"
          description="IP whitelist functionality is currently disabled. All IP addresses can access the admin panel. You can enable it in Settings."
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
          action={
            <Button size="small" onClick={() => navigate('/settings')}>
              Go to Settings
            </Button>
          }
        />
      )}

      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <SafetyOutlined style={{ marginRight: '12px' }} />
          IP Whitelist Management
        </Title>
        <Text type="secondary">
          Manage IP addresses that are allowed to access the admin panel
        </Text>
      </div>

      {/* Statistics */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total IPs"
                value={stats.total}
                prefix={<SafetyOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Active IPs"
                value={stats.active}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Inactive IPs"
                value={stats.inactive}
                valueStyle={{ color: '#cf1322' }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Expired IPs"
                value={stats.expired}
                valueStyle={{ color: '#fa8c16' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Failed Login Statistics */}
      {failedLoginStats && (
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={24}>
            <Card title="Failed Login Attempts" extra={<ExclamationCircleOutlined />}>
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic
                    title="Total Failed Logins"
                    value={failedLoginStats.summary?.totalFailedLogins || 0}
                    prefix={<ExclamationCircleOutlined />}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Unique Failed IPs"
                    value={failedLoginStats.summary?.uniqueIPs || 0}
                    prefix={<GlobalOutlined />}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Avg Failed Logins"
                    value={Math.round(failedLoginStats.summary?.avgFailedLogins || 0)}
                    prefix={<BarChartOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Max Failed Logins"
                    value={failedLoginStats.summary?.maxFailedLogins || 0}
                    prefix={<FireOutlined />}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      {/* Actions */}
      <Card style={{ marginBottom: '24px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                Add IP to Whitelist
              </Button>
                      <Button
                        icon={<SearchOutlined />}
                        onClick={() => setCheckIpModalVisible(true)}
                      >
                        Check IP Status
                      </Button>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={handleBulkDelete}
                        disabled={selectedRowKeys.length === 0}
                      >
                        Delete Selected ({selectedRowKeys.length})
                      </Button>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={() => {
                          setRefreshKey(prev => prev + 1);
                          refetch();
                        }}
                        loading={isLoading}
                      >
                        Force Refresh
                      </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card>
        {error ? (
          <Alert
            message="Error"
            description="Failed to load whitelist data. Please try again."
            type="error"
            showIcon
            action={
              <Button size="small" onClick={() => window.location.reload()}>
                Retry
              </Button>
            }
          />
        ) : (
          <>
            {/* Filter Section */}
            <Card style={{ marginBottom: '16px' }}>
              <Row gutter={[16, 16]} align="bottom">
                <Col xs={24} sm={8} md={6}>
                  <div>
                    <Text strong>Status:</Text>
                    <Select
                      placeholder="All Status"
                      style={{ width: '100%', marginTop: '4px' }}
                      value={filters.status}
                      onChange={(value) => handleFilterChange('status', value)}
                      allowClear
                    >
                      <Option value="active">Active</Option>
                      <Option value="inactive">Inactive</Option>
                    </Select>
                  </div>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <div>
                    <Text strong>Type:</Text>
                    <Select
                      placeholder="All Types"
                      style={{ width: '100%', marginTop: '4px' }}
                      value={filters.type}
                      onChange={(value) => handleFilterChange('type', value)}
                      allowClear
                    >
                      <Option value="ipv4">IPv4</Option>
                      <Option value="ipv6">IPv6</Option>
                    </Select>
                  </div>
                </Col>
                <Col xs={24} sm={8} md={8}>
                  <div>
                    <Text strong>Search IP:</Text>
                    <Input
                      placeholder="Search by IP address (min 3 chars)"
                      style={{ marginTop: '4px' }}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      prefix={<SearchOutlined />}
                      allowClear
                    />
                  </div>
                </Col>
                <Col xs={24} sm={24} md={4}>
                  <Button 
                    onClick={handleClearFilters}
                    icon={<ReloadOutlined />}
                    style={{ 
                      width: '100%',
                      background: '#ff7875',
                      borderColor: '#ff7875',
                      color: '#fff'
                    }}
                    loading={isFetching}
                  >
                    Clear Filters
                  </Button>
                </Col>
              </Row>
            </Card>
            
            <Table
              columns={columns}
              dataSource={whitelistIps}
              rowKey="_id"
              loading={isLoading || isFetching}
              rowSelection={{
                selectedRowKeys,
                onChange: setSelectedRowKeys,
                getCheckboxProps: (record) => ({
                  name: record.ip,
                }),
              }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} IPs`,
              }}
              locale={{
                emptyText: 'No whitelist IPs found'
              }}
            />
          </>
        )}
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingIp ? 'Edit Whitelist IP' : 'Add IP to Whitelist'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false);
          setEditingIp(null);
          form.resetFields();
        }}
        confirmLoading={createMutation.isLoading || updateMutation.isLoading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            type: 'single',
            isActive: true,
          }}
        >
          <Form.Item
            name="ip"
            label="IP Address"
            rules={[
              { required: true, message: 'Please enter IP address' },
              {
                pattern: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(?:\/(?:[0-9]|[1-2][0-9]|3[0-2]))?$/,
                message: 'Please enter a valid IP address or CIDR range',
              },
            ]}
          >
            <Input placeholder="192.168.1.1 or 192.168.1.0/24" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input placeholder="Optional description for this IP" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Type"
              >
                <Select>
                  <Option value="single">Single IP</Option>
                  <Option value="range">IP Range (CIDR)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isActive"
                label="Status"
                valuePropName="checked"
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="expiresAt"
            label="Expiration Date (Optional)"
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              placeholder="Select expiration date"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Check IP Modal */}
      <Modal
        title="Check IP Status"
        open={checkIpModalVisible}
        onOk={handleCheckIp}
        onCancel={() => {
          setCheckIpModalVisible(false);
          setCheckIpValue('');
          setCheckResult(null);
        }}
        confirmLoading={checkIpMutation.isLoading}
      >
        <Form layout="vertical">
          <Form.Item label="IP Address">
            <Input
              placeholder="Enter IP address to check"
              value={checkIpValue}
              onChange={(e) => setCheckIpValue(e.target.value)}
            />
          </Form.Item>
        </Form>

        {checkResult && (
          <div style={{ marginTop: '16px' }}>
            <Divider />
            <Alert
              message={checkResult.message}
              type={checkResult.isWhitelisted ? 'success' : 'error'}
              icon={checkResult.isWhitelisted ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              showIcon
            />
          </div>
        )}
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal
        title="Confirm Bulk Delete"
        open={bulkDeleteModalVisible}
        onOk={confirmBulkDelete}
        onCancel={() => setBulkDeleteModalVisible(false)}
        confirmLoading={bulkDeleteMutation.isLoading}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text strong>Are you sure you want to delete {selectedRowKeys.length} IP(s)?</Text>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">This action cannot be undone.</Text>
        </div>
        <div>
          <Text strong>Selected IPs:</Text>
          <ul style={{ marginTop: '8px', maxHeight: '200px', overflowY: 'auto' }}>
            {selectedRowKeys.map((key) => {
              const ip = whitelistIps.find(item => item._id === key);
              return (
                <li key={key}>
                  {ip?.ip} - {ip?.description || 'No description'}
                </li>
              );
            })}
          </ul>
        </div>
      </Modal>
    </div>
  );
};

export default Whitelist;
